import { createSlice } from '@reduxjs/toolkit';
import api from '../services';

/** Gateway path for templates-service, minus the /v1 the axios base url supplies. */
const TEMPLATES = '/zervio/templates';

/**
 * Templates mirror forms: a template owns an ordered list of versions, one of which is published.
 * Endpoints are served by templates-service through the gateway at /v1/zervio/templates/**
 * (see .helm/services/templates-service/routes/application.yaml). The axios base url already
 * carries /v1, so every path below is relative to that.
 *
 * The one deviation from forms: a form version's content lives under
 * /versions/{id}/form_attributes|panels|panel_widgets, while a template version is a single
 * Handlebars body, saved with PUT {TEMPLATES}/{templateId}/versions/{versionId}.
 */
export const templatesSlice = createSlice({
  name: 'templates',
  initialState: {
    templatesList: [],
    templateOrganizations: [],
  },
  reducers: {
    setTemplatesList: (state, { payload }) => {
      state.templatesList = payload;
    },
    setTemplateDetails: (state, { payload }) => {
      state.templateDetails = payload;
    },
    setTemplateVersions: (state, { payload }) => {
      if (payload.merge) {
        const mergeData = {
          ...payload?.data,
          content: [...state?.templateVersions?.content, ...payload?.data?.content],
        };
        state.templateVersions = mergeData;
      } else {
        state.templateVersions = payload.data;
      }
    },
    setTemplateVersionDetails: (state, { payload }) => {
      state.templateVersionDetails = payload;
    },
    setTemplateOrganizations: (state, { payload }) => {
      state.templateOrganizations = payload;
    },
  },
});

export const getTemplateList = payload => async dispatch => {
  const { search, page } = payload.params;
  const url = `${TEMPLATES}?search=${search ? search : ''}&page=${page ? page : 0}&size=13&sort=name`;
  const { data } = await api.get(url);
  dispatch(setTemplatesList(data));
  return Promise.resolve(data);
};

export const getTemplateDetails = payload => async dispatch => {
  try {
    const { id } = payload;
    const { data } = await api.get(`${TEMPLATES}/${id}`);
    dispatch(setTemplateDetails(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createTemplate = payload => async () => {
  try {
    const { request } = payload;
    const { data } = await api.post(TEMPLATES, request);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateTemplate = payload => async () => {
  try {
    const { id, request } = payload;
    const { data } = await api.put(`${TEMPLATES}/${id}`, request);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Organisations a template is shared with - the same three calls forms uses, and the same
 * FORMS_ORGANIZATION_MANAGE authority behind them.
 */
export const getTemplateOrganizations = payload => async dispatch => {
  try {
    const { id } = payload;
    const { data } = await api.get(`${TEMPLATES}/${id}/organizations`);
    dispatch(setTemplateOrganizations(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addTemplateOrganization = payload => async () => {
  try {
    const { id, body } = payload;
    const { data } = await api.post(`${TEMPLATES}/${id}/organizations`, body);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteTemplateOrganization = payload => async () => {
  try {
    const { id, orgId } = payload;
    const { data } = await api.delete(`${TEMPLATES}/${id}/organizations/${orgId}`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getTemplateVersions = payload => async dispatch => {
  try {
    const { id, params, merge } = payload;
    const { data } = await api.get(`${TEMPLATES}/${id}/versions`, {
      params: params,
    });
    dispatch(setTemplateVersions({ data, merge }));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getTemplateVersionDetails = payload => async dispatch => {
  try {
    const { templateId, versionId } = payload;
    const { data } = await api.get(`${TEMPLATES}/${templateId}/versions/${versionId}`);
    dispatch(setTemplateVersionDetails(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

/** Saves the Handlebars body the editor is holding. */
export const updateTemplateVersion = payload => async () => {
  try {
    const { templateId, versionId, body } = payload;
    const { data } = await api.put(`${TEMPLATES}/${templateId}/versions/${versionId}`, body);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Renders the version to pdf server-side, through the same Playwright/Chromium path the generator
 * uses - so the download is the real document, not the browser's own print output.
 */
export const generateTemplateVersionPdf = payload => async () => {
  try {
    const { templateId, versionId, body } = payload;
    const { data } = await api.post(`${TEMPLATES}/${templateId}/versions/${versionId}/pdf`, body, {
      responseType: 'blob',
    });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const duplicateTemplateVersion = payload => async () => {
  try {
    const { templateId, versionId } = payload;
    const { data } = await api.post(`${TEMPLATES}/${templateId}/versions/${versionId}/duplicate`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const publishTemplateVersion = payload => async () => {
  try {
    const { templateId, versionId } = payload;
    const { data } = await api.put(`${TEMPLATES}/${templateId}/versions/${versionId}/publish`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const archiveTemplateVersion = payload => async () => {
  try {
    const { templateId, versionId } = payload;
    const { data } = await api.put(`${TEMPLATES}/${templateId}/versions/${versionId}/archive`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const rePublishTemplateVersion = payload => async () => {
  try {
    const { templateId, versionId } = payload;
    const { data } = await api.put(`${TEMPLATES}/${templateId}/versions/${versionId}/revert`);
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const {
  setTemplatesList,
  setTemplateDetails,
  setTemplateVersions,
  setTemplateVersionDetails,
  setTemplateOrganizations,
} = templatesSlice.actions;
export default templatesSlice.reducer;
